import ProgramEvidencePublication from '../components/ProgramEvidencePublication';
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FAIR_INVESTMENT_CONFIG } from "../config/fairSettings";
import { formatMoney } from "../utils/formatters";
import axios from "axios";
import { useAccess } from "../access/AccessContext";

import {
  getPlatformSettings,
  fetchPlatformSettings,
  getFairSeedConfig,
} from "../lib/platformSettings";
import { calculateGovernancePosture } from "../lib/governancePosture";
import LoadingOrbit from "../components/LoadingOrbit";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ReferenceDot,
  ReferenceArea,
} from "recharts";

/* ------------------ helpers ------------------ */


const formatPct = (v) => `${Number(v || 0).toFixed(1)}%`;
const formatROI = (v) => `${Number(v || 0).toFixed(1)}x`;

const getRoiLabel = (roi) => {
  if (roi >= 2) return "Exceptional";
  if (roi >= 1) return "Strong";
  if (roi >= 0.3) return "Positive";
  if (roi > 0) return "Marginal";
  return "Negative";
};

function getToneText(tone) {
  switch (tone) {
    case "critical":
      return "text-red-900";
    case "warning":
      return "text-amber-800";
    case "info":
      return "text-blue-800";
    case "success":
      return "text-green-800";
    default:
      return "text-gray-900";
  }
}

function getCorrelationStyle(severity) {
  if (severity === "critical") return "bg-red-100 text-red-700 border-red-200";
  if (severity === "high") return "bg-orange-100 text-orange-700 border-orange-200";
  if (severity === "medium") return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-slate-100 text-slate-600 border-slate-200";
}

function getRecommendationStyle(priority) {
  if (priority === "critical") return "border-red-200 bg-red-50 text-red-700";
  if (priority === "high") return "border-amber-200 bg-amber-50 text-amber-700";
  if (priority === "opportunity") return "border-green-200 bg-green-50 text-green-700";
  return "border-blue-200 bg-blue-50 text-blue-700";
}

function getAppetiteColor(status) {
  if (status === "critical") {
    return {
      bg: "bg-red-50",
      border: "border-red-200",
      text: "text-red-700",
      badge: "bg-red-100 text-red-700",
    };
  }

  if (status === "warning") {
    return {
      bg: "bg-amber-50",
      border: "border-amber-200",
      text: "text-amber-700",
      badge: "bg-amber-100 text-amber-700",
    };
  }

  return {
    bg: "bg-green-50",
    border: "border-green-200",
    text: "text-green-700",
    badge: "bg-green-100 text-green-700",
  };
}

function LoadingSection({ message = "Loading analysis data..." }) {
  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800">
      {message}
    </div>
  );
}

const EMPTY_SIMULATION = {
  empty: false,
  investment: 0,
  baseline: {
    expected_annual_loss: 0,
    p90: 0,
    p95: 0,
  },
  adjusted: {
    expected_annual_loss: 0,
    p90: 0,
    p95: 0,
  },
  impact: {
    eal_reduction: 0,
    eal_reduction_pct: 0,
    p90_reduction: 0,
    p90_reduction_pct: 0,
    p95_reduction: 0,
    p95_reduction_pct: 0,
    roi: 0,
    net_benefit: 0,
  },
  allocation: [],
  curves: {
    baseline: [],
    adjusted: [],
  },
  recommendations: [],
  stress_tests: [],
  correlation_matrix: [],
  risk_evolution: null,
  appetite: {
    overall_status: "healthy",
    metrics: {},
  },
};

export default function StrategicAnalysisWorkspace() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const access = useAccess();
  const canPublish = access.hasPermission("digital_risk.add_portfoliosnapshot");
  const programReference = searchParams.get("program");
  const returnTo = searchParams.get("returnTo");
  const safeReturnTo = returnTo?.startsWith("/") && !returnTo.startsWith("//") ? returnTo : null;
  const [programContext, setProgramContext] = useState(null);
  const [programError, setProgramError] = useState("");
  //const settings = getPlatformSettings();
  const [settings, setSettings] = useState(getPlatformSettings());
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const [pageLoading, setPageLoading] = useState(true);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  

  const [investmentCurveLoading, setInvestmentCurveLoading] = useState(false);

  const [simulation, setSimulation] = useState(EMPTY_SIMULATION);
  const [investmentCurve, setInvestmentCurve] = useState([]);

  const [investment, setInvestment] = useState(0);
  const [selectedInvestment, setSelectedInvestment] = useState(0);

  const effectiveInvestment = investment;

  const [reportMode, setReportMode] = useState(false);
  const [presentationMode, setPresentationMode] = useState(false);

  const [showCorrelationDetails, setShowCorrelationDetails] = useState(false);
  const stressTestingNarrative = simulation?.stress_testing_narrative || null;


  useEffect(() => {
    let isActive = true;

    const loadSettings = async () => {
      try {
        const freshSettings = await fetchPlatformSettings();

        if (!isActive) return;

        setSettings(freshSettings);
      } catch (err) {
        console.error("Error loading platform settings:", err);
      } finally {
        if (isActive) {
          setSettingsLoaded(true);
        }
      }
    };

    loadSettings();

    return () => {
      isActive = false;
    };
  }, []);


  useEffect(() => {
    if (!programReference) { setProgramContext(null); return; }
    let active = true;
    axios.get(`/api/digital-risk/investment-programs/${encodeURIComponent(programReference)}/`)
      .then(({ data }) => { if (active) { setProgramContext(data); setProgramError(""); } })
      .catch(() => { if (active) { setProgramContext(null); setProgramError("Program context could not be resolved."); } });
    return () => { active = false; };
  }, [programReference]);

  const loadedInvestmentCurve = useRef(false);
  const analysisCache = useRef({});
  const initialAnalysisLoaded = useRef(false);

  const loadedInvestmentCurveMax = useRef(null);


  const formatDeltaMillions = (value) => {
    const number = Number(value || 0);
    const sign = number > 0 ? "+" : number < 0 ? "-" : "";
    const absNumber = Math.abs(number);

    if (absNumber >= 1_000_000) {
      return `${sign}$${(absNumber / 1_000_000).toFixed(1)}M`;
    }

    if (absNumber >= 1_000) {
      return `${sign}$${(absNumber / 1_000).toFixed(0)}K`;
    }

    return `${sign}$${absNumber.toFixed(0)}`;
  };

  const investmentStep = Number(settings?.investmentStep || 50000);
  const portfolioEalForCeiling = Number(
    simulation?.baseline?.expected_annual_loss ??
    simulation?.expected_annual_loss ??
    0
  );

  const investmentTolerance = Number(settings?.investmentTolerance || 200000);
  const maxEfficientInvestment = Number(settings?.maxEfficientInvestment || 3000000);
  const maxRecommendedRange = Number(settings?.maxRecommendedRange || 2500000);

  const recommendedRangeMinRatio = Number(settings?.recommendedRangeMinRatio || 0.20);
  const recommendedRangeMaxRatio = Number(settings?.recommendedRangeMaxRatio || 0.30);

  const recommendedRangeDefaultMin = Math.round(
    portfolioEalForCeiling * recommendedRangeMinRatio
  );
  const recommendedRangeDefaultMax = Math.round(
    portfolioEalForCeiling * recommendedRangeMaxRatio
  );

  const investmentCeilingRatio = Number(settings?.investmentCeilingRatio || 0.75);



  const dynamicInvestmentMax = Math.max(
    FAIR_INVESTMENT_CONFIG.max,
    Math.ceil(
      (portfolioEalForCeiling * investmentCeilingRatio) / investmentStep
    ) * investmentStep
  );


  /* ---------- investment curve derived data ---------- */

  const curveWithCurrent = useMemo(() => {
    return Array.isArray(investmentCurve) ? investmentCurve : [];
  }, [investmentCurve]);

  const diminishingPoint = useMemo(() => {
    if (!curveWithCurrent || curveWithCurrent.length < 3) return null;

    const sorted = [...curveWithCurrent]
      .filter((p) => Number(p.investment || 0) > 0)
      .sort((a, b) => Number(a.investment || 0) - Number(b.investment || 0));

    for (let i = 1; i < sorted.length; i += 1) {
      const prev = sorted[i - 1];
      const curr = sorted[i];

      const deltaInvestment = Number(curr.investment || 0) - Number(prev.investment || 0);
      const deltaReduction = Number(curr.riskReduction || 0) - Number(prev.riskReduction || 0);

      if (deltaInvestment <= 0) continue;

      const marginalROI = deltaReduction / deltaInvestment;
      if (marginalROI < 2) return prev;
    }

    return null;
  }, [curveWithCurrent]);

  const optimalPoint = useMemo(() => {
    if (!curveWithCurrent || curveWithCurrent.length === 0) return null;

    const candidates = curveWithCurrent.filter((p) => Number(p.investment || 0) > 0);
    if (candidates.length === 0) return null;

    return candidates.reduce((best, current) =>
      Number(current.netBenefit || 0) > Number(best.netBenefit || 0) ? current : best
    );
  }, [curveWithCurrent]);

  const recommendedRange = useMemo(() => {
    if (!curveWithCurrent || curveWithCurrent.length < 3) return null;

    const sorted = [...curveWithCurrent]
      .filter((p) => Number(p.investment || 0) > 0)
      .sort((a, b) => Number(a.investment || 0) - Number(b.investment || 0));

    if (sorted.length === 0) return null;

    const candidates = sorted.filter(
      (p) =>
        Number(p.roi || 0) >= 3 &&
        Number(p.riskReduction || 0) >=
          Number(settings?.criticalExposureThreshold || 5_000_000) &&
        Number(p.investment || 0) <= maxEfficientInvestment
    );

    if (candidates.length === 0) {
      return {
        min: recommendedRangeDefaultMin,
        max: recommendedRangeDefaultMax,
        center:
          (recommendedRangeDefaultMin + recommendedRangeDefaultMax) / 2,
      };
    }

    const best = candidates.reduce((a, b) =>
      Number(b.roi || 0) > Number(a.roi || 0) ? b : a
    );

    const rawMin = Math.max(
      investmentStep * 10,
      Number(diminishingPoint?.investment || 0) ||
        Number(best.investment || 0) - (investmentStep * 10)
    );

    const rawMax = Math.min(
      maxRecommendedRange,
      Number(best.investment || 0) + (investmentStep * 10)
    );

    return {
      min: Math.min(rawMin, rawMax),
      max: Math.max(rawMin, rawMax),
      center: Number(best.investment || 0),
    };
  }, [
    curveWithCurrent,
    diminishingPoint,
    settings,
    investmentStep,
    maxEfficientInvestment,
    maxRecommendedRange,
    recommendedRangeDefaultMin,
    recommendedRangeDefaultMax,
  ]);


  useEffect(() => {
    console.log("recommendedRange =", recommendedRange);
    console.log("curve length =", curveWithCurrent?.length);
    console.log("optimalPoint =", optimalPoint);
    console.log("diminishingPoint =", diminishingPoint);
  }, [
    recommendedRange,
    curveWithCurrent,
    optimalPoint,
    diminishingPoint,
  ]);

  /* ---------- load portfolio analysis ---------- */

  useEffect(() => {
    let cancelled = false;

    const loadAnalysis = async () => {
      if (!settingsLoaded) return;

      const simulations = Number(settings?.simulations || 5000);

      const seed = getFairSeedConfig(settings).seed;

      const cacheKey = [
        investment,
        simulations,
        seed,
        settings.seedMode,
        settings.currency,
      ].join(":");

     /* const cacheKey = `${debouncedInvestment}:${simulations}:${seed}`;  */

      if (!settingsLoaded) return;

      
      if (analysisCache.current[cacheKey]) {
        setSimulation(analysisCache.current[cacheKey]);

        if (!initialAnalysisLoaded.current) {
          initialAnalysisLoaded.current = true;
          setPageLoading(false);
        }

        return;
      }
      

      setAnalysisLoading(true);

      try {
        const simulationResponse = await axios.get(
          "/api/digital-risk/dashboard/risk-portfolio-analysis/",
            {
              params: {
                investment,
                simulations,
                seed,
              },
            }
          );

        if (cancelled) return;

        const payload = simulationResponse.data || EMPTY_SIMULATION;

        analysisCache.current[cacheKey] = payload;
        setSimulation(payload);

        console.log(
          "Strategic Analysis Impact",
          payload?.impact?.eal_reduction
        );


      } catch (error) {
        if (!cancelled) setSimulation(EMPTY_SIMULATION);
      } finally {
        if (!cancelled) {
          setAnalysisLoading(false);

          if (!initialAnalysisLoaded.current) {
            initialAnalysisLoaded.current = true;
            setPageLoading(false);
          }
        }
      }
    };

    loadAnalysis();

    return () => {
      cancelled = true;
    };

    }, [
    settingsLoaded,
    investment,
    settings.simulations,
    settings.seedMode,
    settings.seedValue,
  ]);

  /* ---------- lazy load investment curve ---------- */

  useEffect(() => {
    if (!settingsLoaded) return;
    if (
      loadedInvestmentCurve.current &&
      loadedInvestmentCurveMax.current === dynamicInvestmentMax
    ) {
      return;
    }

    let timer;
    let cancelled = false;

    const loadInvestmentCurve = async () => {
      setInvestmentCurveLoading(true);

      try {
       

        const curveSimulations = Math.min(
          Number(settings?.simulations || 5000),
          Number(settings?.curveSimulations || 3000)
        );

        const seed = getFairSeedConfig(settings).seed;


        const res = await axios.get(
          "/api/digital-risk/dashboard/fair-portfolio-investment-curve/",
          {
            params: {
              simulations: curveSimulations,
              seed,
              max_investment: dynamicInvestmentMax,
            },
          }
        );

        console.log("Investment curve response:", res.data);

        if (cancelled) return;

        setInvestmentCurve(
          Array.isArray(res.data?.curve) ? res.data.curve : []
        );
        loadedInvestmentCurve.current = true;
        loadedInvestmentCurveMax.current = dynamicInvestmentMax;
      } catch (err) {
        console.error(
          "Error loading investment curve:",
          err?.response?.data || err
        );
      } finally {
        if (!cancelled) setInvestmentCurveLoading(false);
      }
    };
    timer = setTimeout(loadInvestmentCurve, 2000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    settingsLoaded,
    settings.seedMode,
    settings.seedValue,
    dynamicInvestmentMax,
  ]);

  /* ---------- fullscreen ---------- */

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) setPresentationMode(false);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  /* ---------- empty ---------- */

  if (simulation?.empty) {
    return (

      <div className="p-6 space-y-6">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-slate-400">Strategic Intelligence</p>
          <h1 className="text-3xl font-bold">Risk Analysis Workspace</h1>
          <p className="text-gray-500 mt-1">
            Analyze portfolio exposure, concentration, systemic dependencies, stress conditions and risk evolution.
          </p>
        </div>

        <div className="bg-white border rounded-2xl p-10 text-center">
          <h2 className="text-2xl font-semibold text-gray-900">No Active Risk Scenarios</h2>
          <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
            The strategic analysis workspace requires active FAIR risk scenarios to generate portfolio insights, stress tests, correlation analysis and investment simulations.
          </p>
          <div className="mt-6 text-sm text-gray-400">
            Create and activate FAIR scenarios to begin strategic analysis.
          </div>
        </div>
      </div>
    );
  }

  /* ---------- portfolio analysis derived data ---------- */

  const eal = Number(simulation?.adjusted?.expected_annual_loss ?? 0);
  const p95 = Number(simulation?.adjusted?.p95 ?? 0);
  const baselineEal = Number(simulation?.baseline?.expected_annual_loss ?? 0);
  const baselineP95 = Number(simulation?.baseline?.p95 ?? 0);

  const ealReduction = Number(simulation?.impact?.eal_reduction ?? 0);
  const reductionPct = Number(simulation?.impact?.eal_reduction_pct ?? 0);
  const roi = Number(simulation?.impact?.roi ?? 0);

  const appetite = simulation?.appetite || {};
  const appetiteMetrics = appetite?.metrics || {};
  const overallAppetiteStatus = appetite?.overall_status || "healthy";

  const recommendations = Array.isArray(simulation?.recommendations) ? simulation.recommendations : [];
  const stressTests = Array.isArray(simulation?.stress_tests) ? simulation.stress_tests : [];
  const correlationMatrix = Array.isArray(simulation?.correlation_matrix) ? simulation.correlation_matrix : [];
  const correlation = simulation?.correlation || {};

  const dependencyConcentration =
    simulation?.dependency_concentration || {};

  const topSystemicDependency =
    dependencyConcentration?.top_dependency || null;

  const systemicDependencies =
    Array.isArray(dependencyConcentration?.dependencies)
      ? dependencyConcentration.dependencies
      : [];

  const correlationImpactPct = Number(
    correlation?.correlation_impact_pct || 0
  );

  let systemicRiskLabel = "Low";

  if (correlationImpactPct >= 25) {
    systemicRiskLabel = "High";
  } else if (correlationImpactPct >= 10) {
    systemicRiskLabel = "Moderate";
  }

  const systemicRiskSubtitle = correlation?.enabled
    ? `+${correlationImpactPct.toFixed(1)}% P95 uplift from shared dependencies`
    : "Shared dependency shocks not applied";

  const appetiteBreaches = Object.entries(appetiteMetrics).filter(([, metric]) => metric?.status === "critical");
  const appetiteWarnings = Object.entries(appetiteMetrics).filter(([, metric]) => metric?.status === "warning");
  const appetiteStyle = getAppetiteColor(overallAppetiteStatus);

  const portfolioScenarios = Array.isArray(simulation?.allocation) ? simulation.allocation : [];

  const contributors = portfolioScenarios
    .map((item) => ({
      ...item,
      scenario_id: item.scenario_id,
      incident_type: item.incident_type || "Unknown scenario",
      asset_name: item.asset_name || "Unknown asset",
      expected_annual_loss: Number(item.adjusted_eal ?? item.baseline_eal ?? 0),
      baseline_eal: Number(item.baseline_eal ?? 0),
      adjusted_eal: Number(item.adjusted_eal ?? item.baseline_eal ?? 0),
      eal_reduction: Number(item.eal_reduction ?? 0),
      allocated_investment: Number(item.allocated_investment ?? 0),
      eal_contribution_pct: Number(item.weight_pct ?? item.weight ?? 0),
      weight_pct: Number(item.weight_pct ?? item.weight ?? 0),
    }))
    .sort((a, b) => Number(b.expected_annual_loss || 0) - Number(a.expected_annual_loss || 0));

  const topScenario = contributors[0] || null;
  const top1 = Number(topScenario?.eal_contribution_pct || topScenario?.weight_pct || 0);
  const hasPortfolioData = contributors.length > 0 && eal > 0;

  const top3 = contributors
    .slice(0, 3)
    .reduce((sum, x) => sum + Number(x.eal_contribution_pct || x.weight_pct || 0), 0);

  const concentration =
    top1 >= 50 ? "High concentration" : top1 >= 35 ? "Moderate concentration" : "Distributed risk";

  const concentrationStatus = appetiteMetrics?.top_concentration?.status || "healthy";

  let concentrationLabel = "Low";
  let concentrationSubtitle = "Portfolio diversified";

  if (top3 >= 75) {
    concentrationLabel = "High";
    concentrationSubtitle = `${formatPct(top3)} concentrated in top 3 scenarios`;
  } else if (top3 >= 50) {
    concentrationLabel = "Moderate";
    concentrationSubtitle = `${formatPct(top3)} concentrated in top 3 scenarios`;
  }

  const optimalInvestment = Number(optimalPoint?.investment || 0);
  const optimalReduction = Number(optimalPoint?.riskReduction || 0);

  const normalizeInvestment = (value) => {
    return Math.round(Number(value || 0) / investmentStep) * investmentStep;
  };

  const investmentStatus = (() => {
    if (!recommendedRange || !investment) return null;

    const { min, max } = recommendedRange;
    const tolerance = investmentTolerance;

    if (investment >= min - tolerance && investment <= max + tolerance) {
      return {
        status: "optimal",
        label: "Within optimal range",
        color: "green",
      };
    }

    if (investment < min - tolerance) {
      return {
        status: "underfunded",
        label: "Underinvested",
        color: "blue",
      };
    }

    if (
      investment > max + tolerance &&
      investment <= max + investmentTolerance * 5
    ) {
      return {
        status: "overfunded",
        label: "Slightly above optimal",
        color: "yellow",
      };
    }

    if (investment > max + investmentTolerance * 5) {
      return {
        status: "overfunded",
        label: "Overinvested",
        color: "red",
      };
    }

    return {
      status: "review",
      label: "Review",
      color: "gray",
    };
  })();


  const investmentDecision = (() => {
    if (!recommendedRange) {
      return {
        title: "Investment analysis unavailable",
        description: "Investment efficiency range could not be calculated.",
        badge: "Loading",
        tone: "gray",
      };
    }

    if (investment <= 0) {
      return {
        title: "Funding range should be evaluated",
        description: `Recommended investment range is ${formatMoney(
          recommendedRange.min,
          settings.currency
        )}–${formatMoney(
          recommendedRange.max,
          settings.currency
        )}. Portfolio risk reduction opportunities remain unrealized.`,
        badge: "Evaluate",
        tone: "blue",
      };
    }

    if (!investmentStatus) {
      return {
        title: "Investment position could not be evaluated",
        description:
          "Current investment could not be compared against the recommended efficiency range.",
        badge: "Review",
        tone: "gray",
      };
    }

    if (investmentStatus.status === "optimal") {
      return {
        title: "Investment is aligned with efficient range",
        description:
          "Current funding is expected to deliver risk reduction within the recommended efficiency band.",
        badge: "Optimal",
        tone: "green",
      };
    }

    if (investmentStatus.status === "underfunded") {
      return {
        title: "Funding remains below recommended level",
        description:
          "Current investment is below the efficient range and may leave avoidable portfolio exposure unaddressed.",
        badge: "Underfunded",
        tone: "amber",
      };
    }

    if (investmentStatus.status === "overfunded") {
      return {
        title: "Investment exceeds efficient range",
        description:
          "Additional investment may generate diminishing risk-reduction returns.",
        badge: "Overfunded",
        tone: "red",
      };
    }

    return {
      title: "Investment position requires review",
      description:
        "Current funding should be reviewed against the recommended efficiency range.",
      badge: "Review",
      tone: "gray",
    };
  })();

  const investmentDecisionStyle = {
    blue: {
      bg: "bg-blue-50",
      border: "border-blue-200",
      text: "text-blue-900",
      badge: "bg-blue-100 text-blue-700",
    },
    amber: {
      bg: "bg-amber-50",
      border: "border-amber-200",
      text: "text-amber-900",
      badge: "bg-amber-100 text-amber-700",
    },
    green: {
      bg: "bg-green-50",
      border: "border-green-200",
      text: "text-green-900",
      badge: "bg-green-100 text-green-700",
    },
    red: {
      bg: "bg-red-50",
      border: "border-red-200",
      text: "text-red-900",
      badge: "bg-red-100 text-red-700",
    },
    gray: {
      bg: "bg-slate-50",
      border: "border-slate-200",
      text: "text-slate-900",
      badge: "bg-slate-100 text-slate-700",
    },
  }[investmentDecision.tone];

  const investmentDecisionSummary = (() => {
    if (!recommendedRange) {
      return {
        title: "Investment guidance is not yet available",
        decision: "Evaluate further",
        description:
          "The investment curve is still required to identify an efficient funding range.",
      };
    }

    if (investment <= 0) {
      return {
        title: "Investment decision pending",
        decision: "Evaluate funding range",
        description: `Recommended investment range is ${formatMoney(
          recommendedRange.min,
          settings.currency
        )}–${formatMoney(
          recommendedRange.max,
          settings.currency
        )}. Use the slider to test expected loss reduction and ROI.`,
      };
    }

    return {
      title: "Investment decision summary",
      decision: investmentStatus?.label || "Scenario evaluated",
      description: `Current investment of ${formatMoney(
        investment,
        settings.currency
      )} reduces expected annual loss by ${formatMoney(
        ealReduction,
        settings.currency
      )} (${formatPct(reductionPct)}), with estimated ROI of ${formatROI(roi)}.`,
    };
  })();


  const criticalRisks = contributors.filter(
    (risk) => Number(risk.expected_annual_loss || 0) >= Number(settings.criticalExposureThreshold || 0)
  ).length;

  const governancePosture = calculateGovernancePosture({
    criticalRisks,
    delayedInitiatives: 0,
    avgMitigationProgress: 75,
    mttr: 6,
    settings,
  });

  const calculatedGovernanceScore = governancePosture?.score ?? 0;
  const governanceLevel = governancePosture?.level || "Stable";

  const generateInsight = () => {
    const scenario = topScenario || contributors?.[0] || null;

    if (!scenario) {
      return analysisLoading
        ? "Running portfolio analysis. Strategic insight will update once quantified scenarios are available."
        : "No quantified portfolio scenarios are available for strategic insight generation.";
    }

    const scenarioAsset =
      scenario.asset?.name ||
      scenario.asset ||
      "Unknown asset";

    const correlationImpact =
      simulation?.correlation?.correlation_impact_pct || 0;

    if (investment > 0 && simulation) {
      return `Expected annual loss is ${formatMoney(
        eal,
        settings.currency
      )}. Portfolio concentration remains ${concentration.toLowerCase()}, with ${formatPct(
        top1
      )} driven by ${scenario.incident_type} → ${scenarioAsset}. Shared dependency exposure increases tail-risk by ${correlationImpact.toFixed(
        1
      )}%. An investment of ${formatMoney(
        investment,
        settings.currency
      )} reduces risk by ${formatMoney(
        ealReduction,
        settings.currency
      )} (ROI: ${formatROI(roi)}). ${
        recommendedRange
          ? `Recommended investment range is ${formatMoney(
              recommendedRange.min,
              settings.currency
            )}–${formatMoney(
              recommendedRange.max,
              settings.currency
            )}, balancing impact and capital efficiency.`
          : ""
      }`;
    }

    return `Expected annual loss is ${formatMoney(
      eal,
      settings.currency
    )}. Portfolio concentration remains ${concentration.toLowerCase()}, with ${formatPct(
      top1
    )} driven by ${scenario.incident_type} → ${scenarioAsset}. Shared dependency exposure increases tail-risk by ${correlationImpact.toFixed(
      1
    )}%. Move the investment slider to evaluate risk reduction.`;
  };


  const rankedStressTests = [...stressTests].sort(
    (a, b) => Number(b.delta_pct || 0) - Number(a.delta_pct || 0)
  );

  const topStressTest = rankedStressTests[0] || null;

  const stressBannerStyle =
    topStressTest?.severity === "critical"
      ? "bg-red-50 border-red-100 text-red-900"
      : topStressTest?.severity === "high"
      ? "bg-amber-50 border-amber-100 text-amber-900"
      : topStressTest?.severity === "moderate"
      ? "bg-blue-50 border-blue-100 text-blue-900"
      : "bg-slate-50 border-slate-100 text-slate-900";


  const appetiteItems = [
    {
      key: "EAL",
      label: "Expected Annual Loss",
      metric: appetiteMetrics?.EAL,
      valueFormatter: (value) => formatMoney(value, settings.currency),
    },
    {
      key: "P95",
      label: "Tail Risk P95",
      metric: appetiteMetrics?.P95,
      valueFormatter: (value) => formatMoney(value, settings.currency),
    },
    {
      key: "top_concentration",
      label: "Portfolio Concentration",
      metric: appetiteMetrics?.top_concentration,
      valueFormatter: (value) => `${Number(value || 0).toFixed(1)}%`,
    },
  ].filter((item) => item.metric);

  const appetiteStatusLabel =
    overallAppetiteStatus === "critical"
      ? "Outside Appetite"
      : overallAppetiteStatus === "warning"
      ? "Near Appetite Limit"
      : "Within Appetite";

  const appetiteStatusStyle =
    overallAppetiteStatus === "critical"
      ? "bg-red-50 border-red-100 text-red-900"
      : overallAppetiteStatus === "warning"
      ? "bg-amber-50 border-amber-100 text-amber-900"
      : "bg-green-50 border-green-100 text-green-900";

  const appetiteNarrative =
    overallAppetiteStatus === "critical"
      ? "One or more portfolio risk metrics exceed approved governance tolerance and require executive action."
      : overallAppetiteStatus === "warning"
      ? "Portfolio exposure is approaching approved tolerance levels and should be monitored closely."
      : "Portfolio exposure remains within approved governance tolerance across key risk appetite metrics.";

  /* ------------------ UI ------------------ */

  return (

    <div className="p-6 space-y-6">

    <>
        {pageLoading && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center">
            <LoadingOrbit
              message=""
              submessage=""
            />
          </div>
        )}
      <div className={`space-y-8 ${reportMode ? "print-container" : ""}`}>

        {(programReference || programContext) && <section className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          {programError ? <p role="alert" className="text-red-800">{programError}</p> : programContext ? <><p className="text-xs font-semibold uppercase text-blue-700">Program publication context</p><p className="font-semibold">{programContext.program_name}</p><p className="text-sm">{programContext.program_reference} · {programContext.status} · Board Decision {programContext.board_decision_reference || "Unavailable"}</p><p className="mt-2 text-xs text-slate-600">Program context supplies provenance only; it does not change the active FAIR scenario portfolio.</p></> : <p>Resolving Program context…</p>}
        </section>}
        {canPublish && programContext && <ProgramEvidencePublication key={`${access.username}:${programReference}`}
          program={programContext} ready={!analysisLoading && settingsLoaded && !simulation?.empty}
          returnTo={safeReturnTo} payload={{ investment: effectiveInvestment,
            snapshot_name: `${programContext.program_name} - Current Portfolio Position - ${new Date().toISOString().slice(0, 10)}`,
            investment_program_reference: programContext.program_reference,
            analytical_purpose: 'CURRENT_PORTFOLIO_POSITION', capture_analytical_evidence: true,
            simulations: Number(settings.simulations), seed: getFairSeedConfig(settings).seed ?? null }}>
          <p>Program: {programContext.program_name} · Board Decision: {programContext.board_decision_reference || 'Unavailable'}</p>
          <p>Investment: {formatMoney(effectiveInvestment, settings.currency)} · Currency: {settings.currency}</p>
          <p>Simulations: {settings.simulations} · Seed: {getFairSeedConfig(settings).seed ?? 'Stochastic'}</p>
          <p>Metric contract: Adjusted post-investment EAL/P95 · Current EAL: {formatMoney(eal, settings.currency)} · Current P95: {formatMoney(p95, settings.currency)}</p>
        </ProgramEvidencePublication>}
        <div  className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-slate-400">
              Strategic Intelligence
            </p>
            <h1 className="text-2xl font-semibold text-slate-900 mt-1">
              Risk Analysis Workspace
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Analyze portfolio exposure, concentration, systemic dependencies, stress conditions and risk evolution.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              Monte Carlo: {settings.simulations.toLocaleString()} simulations ·
              Confidence: {settings.confidenceLevel} ·
              Loss model: {settings.lossModel}
            </p>
          </div>
          <div className="text-left xl:text-right mr-4">
            <p className="text-sm text-slate-400">Portfolio Exposure</p>
            <p className="text-4xl font-bold mt-1">{analysisLoading && !hasPortfolioData ? formatMoney(0, settings.currency) : formatMoney(eal, settings.currency)}</p>
          </div>
        </div>

        <div className="no-print flex justify-end gap-3 print:hidden">
          <button
            onClick={() => navigate("/board-dashboard")}
            className="px-4 py-2 rounded-xl text-sm border bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
          >
            ← Board Summary
          </button>

          <button
            onClick={() => setReportMode(!reportMode)}
            className={`px-4 py-2 rounded-xl text-sm border transition flex items-center gap-2 ${
              reportMode
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {reportMode ? "📊 Dashboard Mode" : "📄 Board Report"}
          </button>

          {reportMode && (
            <button
              onClick={() => window.print()}
              className="px-4 py-2 rounded-xl text-sm border bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            >
              🖨️ Print / Save PDF
            </button>
          )}

          <button
            onClick={() => {
              const nextMode = !presentationMode;
              setPresentationMode(nextMode);

              if (nextMode && !document.fullscreenElement) document.documentElement.requestFullscreen();
              if (!nextMode && document.fullscreenElement) document.exitFullscreen();
            }}
            className={`px-4 py-2 rounded-xl text-sm border transition ${
              presentationMode
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {presentationMode ? "Exit Presentation" : "Presentation Mode"}
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-slate-400">Digital Risk Governance Overview</p>
              <h1 className="text-xl md:text-2xl font-semibold mt-2">
                {overallAppetiteStatus === "critical"
                  ? "Risk exceeds board-approved tolerance"
                  : overallAppetiteStatus === "warning"
                  ? "Risk is approaching tolerance limits"
                  : "Risk is operating within approved appetite"}
              </h1>
              <p className="text-sm text-slate-600 mt-2">
                Portfolio exposure, concentration, investment efficiency and resilience posture.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 text-right">
              <div>
                <p className="text-xs text-slate-400 uppercase">EAL</p>
                <p className="text-xl font-semibold">{analysisLoading ? "Loading..." : formatMoney(eal, settings.currency)}</p>
              </div>

              <div>
                <p className="text-xs text-slate-400 uppercase">Breaches</p>
                <p className="text-xl font-semibold">{appetiteBreaches.length}</p>
              </div>

              <div>
                <p className="text-xs text-slate-400 uppercase">Top Risk</p>
                <p className="text-xl font-semibold">{topScenario?.incident_type || "N/A"}</p>
              </div>
            </div>
          </div>
        </div>

        <SectionCard
          title="Risk Appetite Alignment"
          subtitle="Portfolio exposure compared against approved governance tolerance."
        >
          <div className="space-y-5">
            <div className={`rounded-xl border p-4 ${appetiteStatusStyle}`}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wide opacity-70">
                    Overall Appetite Status
                  </p>

                  <h3 className="text-2xl font-semibold mt-1">
                    {appetiteStatusLabel}
                  </h3>

                  <p className="text-sm mt-2">
                    {appetiteNarrative}
                  </p>
                </div>

                <div className="rounded-xl bg-white/70 border px-4 py-3 text-right">
                  <p className="text-xs opacity-70">Metrics evaluated</p>
                  <p className="text-2xl font-semibold">
                    {appetiteItems.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {appetiteItems.map((item) => {
                const metric = item.metric || {};
                const status = metric.status || "healthy";

                const cardClass =
                  status === "critical"
                    ? "bg-red-50 border-red-100"
                    : status === "warning"
                    ? "bg-amber-50 border-amber-100"
                    : "bg-slate-50 border-slate-200";

                const badgeClass =
                  status === "critical"
                    ? "bg-red-100 text-red-700"
                    : status === "warning"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-green-100 text-green-700";

                return (
                  <div key={item.key} className={`rounded-xl border p-4 ${cardClass}`}>
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-semibold text-slate-900">
                        {item.label}
                      </p>

                      <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${badgeClass}`}>
                        {status}
                      </span>
                    </div>

                    <div className="mt-4">
                      <p className="text-xs uppercase tracking-wide text-slate-500">
                        Current
                      </p>

                      <p className="text-xl font-semibold text-slate-900 mt-1">
                        {item.valueFormatter(metric.value)}
                      </p>
                    </div>

                    <div className="mt-3 text-xs text-slate-500">
                      Threshold:{" "}
                      <strong className="text-slate-700">
                        {item.valueFormatter(metric.threshold)}
                      </strong>
                    </div>

                    {Number(metric.breach_pct || 0) > 0 && (
                      <p className="text-xs text-red-700 mt-2">
                        Breach: {Number(metric.breach_pct || 0).toFixed(1)}%
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </SectionCard>

        {recommendations.length > 0 && (
          <SectionCard
            title="Board Recommendations"
            subtitle="Decision-oriented guidance based on exposure, concentration and investment efficiency."
            actions={
              <span className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-600">
                {recommendations.length} insights
              </span>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {recommendations.map((item, index) => (
                <div key={index} className={`rounded-xl border p-4 ${getRecommendationStyle(item.priority)}`}>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-semibold text-sm">{item.title}</h3>
                    <span className="text-xs capitalize font-medium">{item.priority}</span>
                  </div>
                  <p className="text-sm mt-3 text-gray-700">{item.recommendation}</p>
                  <p className="text-xs mt-3 text-gray-500">{item.rationale}</p>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-gray-800">
          {generateInsight()}
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7">
            <Kpi
              title="Expected annual loss"
              value={analysisLoading && !hasPortfolioData ? "Loading..." : formatMoney(eal, settings.currency)}
              tone="critical"
            />

            <Kpi
              title="Tail risk P95"
              value={analysisLoading && !hasPortfolioData ? "Loading..." : formatMoney(p95, settings.currency)}
              tone="critical"
            />
            <Kpi title="Top Risk" value={formatPct(top1)} tone="warning" />
            <Kpi
              title="Risk Concentration"
              value={concentrationLabel}
              subtitle={concentrationSubtitle}
              tone={
                top3 >= 75
                  ? "warning"
                  : top3 >= 50
                  ? "info"
                  : "success"
              }
            />

            <Kpi
              title="Systemic Risk"
              value={systemicRiskLabel}
              subtitle={systemicRiskSubtitle}
              tone={correlation?.enabled ? "warning" : "success"}
            />

            <Kpi title="ROI" value={formatROI(roi)} tone="info" />
            <Kpi title="Reduction EAL" value={formatPct(reductionPct)} tone="success" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          <SectionCard
            title="Top Risk Driver"
            subtitle="Largest contributor to portfolio exposure."
          >
            {topScenario ? (
              <div className="space-y-4">

                <div>
                  <p className="text-lg font-semibold text-slate-900">
                    {topScenario.incident_type}
                  </p>

                  <p className="text-sm text-slate-500 mt-1">
                    {topScenario.asset_name}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">

                  <div className="rounded-xl bg-slate-50 border p-3">
                    <p className="text-xs uppercase tracking-wide text-slate-500">
                      Expected Annual Loss
                    </p>

                    <p className="text-xl font-semibold text-red-700 mt-1">
                      {formatMoney(
                        topScenario.expected_annual_loss,
                        settings.currency
                      )}
                    </p>
                  </div>

                  <div className="rounded-xl bg-slate-50 border p-3">
                    <p className="text-xs uppercase tracking-wide text-slate-500">
                      Portfolio Share
                    </p>

                    <p className="text-xl font-semibold text-amber-700 mt-1">
                      {formatPct(top1)}
                    </p>
                  </div>

                </div>

                <div className="rounded-xl bg-blue-50 border border-blue-100 p-3">
                  <p className="text-sm text-blue-800">
                    Primary contributor to portfolio exposure and executive monitoring priority.
                  </p>
                </div>

              </div>
            ) : (
              <p className="text-sm text-gray-400">
                No top scenario available.
              </p>
            )}
          </SectionCard>

          <SectionCard
            title="Risk Concentration"
            subtitle="Assessment of exposure dependency across the largest scenarios."
          >
            <div className="space-y-4 text-sm text-gray-700">
              <div
                className={`rounded-xl border p-4 ${
                  top3 >= 75
                    ? "bg-amber-50 border-amber-100"
                    : top3 >= 50
                    ? "bg-blue-50 border-blue-100"
                    : "bg-green-50 border-green-100"
                }`}
              >
                <p className="font-semibold text-gray-900">
                  {top3 >= 75
                    ? "High concentration detected"
                    : top3 >= 50
                    ? "Moderate concentration detected"
                    : "Portfolio exposure is broadly distributed"}
                </p>

                <p className="text-sm text-gray-600 mt-2">
                  {top3 >= 75
                    ? `${formatPct(
                        top3
                      )} of quantified exposure is concentrated in the three largest scenarios.`
                    : top3 >= 50
                    ? `${formatPct(
                        top3
                      )} of quantified exposure is concentrated in the three largest scenarios and should be monitored.`
                    : `The three largest scenarios represent ${formatPct(
                        top3
                      )} of quantified exposure.`}
                </p>

                {top3 >= 75 && (
                  <p className="text-xs text-gray-500 mt-2">
                    This increases portfolio sensitivity to control failures, supplier disruption
                    and systemic events.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border bg-slate-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Top Scenario
                  </p>
                  <p className="text-xl font-semibold mt-1">
                    {formatPct(top1)}
                  </p>
                </div>

                <div className="rounded-xl border bg-slate-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Top 3 Scenarios
                  </p>
                  <p className="text-xl font-semibold mt-1">
                    {formatPct(top3)}
                  </p>
                </div>
              </div>

              <p className="text-xs text-gray-500">
                {top3 >= 75
                  ? "Board attention is recommended to reduce concentration risk and improve portfolio resilience."
                  : top3 >= 50
                  ? "Management should monitor whether mitigation coverage remains adequate across the largest scenarios."
                  : "Current exposure is reasonably diversified across the active scenario portfolio."}
              </p>
            </div>
          </SectionCard>

        </div>

        {correlationMatrix.length > 0 && (
          <SectionCard
            title="Systemic Dependency Exposure"
            subtitle="Shared digital dependencies that may amplify portfolio losses."
          >
            <div className="space-y-5">
              {topSystemicDependency && (
                <div className="rounded-2xl border border-amber-100 bg-amber-50 p-5">
                  <p className="text-xs uppercase tracking-wide text-amber-700">
                    Primary Systemic Dependency
                  </p>

                  <h3 className="text-2xl font-semibold text-amber-950 mt-2">
                    {topSystemicDependency.dependency}
                  </h3>

                  <p className="text-sm text-amber-900 mt-2">
                    {topSystemicDependency.scenario_count} scenarios share this dependency.
                    A disruption could affect multiple business services simultaneously.
                  </p>

                  <div className="mt-4 inline-flex rounded-full bg-white border px-3 py-1 text-xs font-semibold text-amber-800">
                    Systemic Risk Level: {topSystemicDependency.severity}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {systemicDependencies.slice(0, 3).map((item) => (
                  <div
                    key={item.dependency}
                    className="rounded-xl border bg-slate-50 p-4"
                  >
                    <p className="text-sm font-semibold text-slate-900">
                      {item.dependency}
                    </p>

                    <p className="text-2xl font-semibold mt-2">
                      {item.scenario_count}
                    </p>

                    <p className="text-xs text-slate-500 mt-1">
                      Scenario(s) affected
                    </p>

                    <span
                      className={`mt-3 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        item.severity === "High"
                          ? "bg-red-100 text-red-700"
                          : item.severity === "Moderate"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-blue-100 text-blue-700"
                      }`}
                    >
                      {item.severity}
                    </span>
                  </div>
                ))}
              </div>

              <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                <p className="text-sm font-semibold text-blue-950">
                  Correlation-aware Monte Carlo simulation is active.
                </p>

                <p className="text-sm text-blue-800 mt-1">
                  Shared dependency shocks are included in portfolio EAL and P95.
                  Current tail-risk uplift from shared dependencies is{" "}
                  <strong>{correlationImpactPct.toFixed(1)}%</strong>.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowCorrelationDetails((prev) => !prev)}
                className="flex w-full items-center justify-between rounded-xl border bg-white px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <span>Correlation detail pairs</span>
                <span className="text-xs text-slate-500">
                  {showCorrelationDetails ? "Hide" : "Show"}
                </span>
              </button>

              {showCorrelationDetails && (
                <div className="overflow-x-auto rounded-xl border">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50 text-left text-xs uppercase tracking-wide text-gray-500">
                        <th className="px-4 py-3">Scenario A</th>
                        <th className="px-4 py-3">Scenario B</th>
                        <th className="px-4 py-3">Shared Dependency</th>
                        <th className="px-4 py-3">Score</th>
                        <th className="px-4 py-3">Severity</th>
                      </tr>
                    </thead>

                    <tbody>
                      {correlationMatrix.slice(0, 10).map((item, index) => (
                        <tr key={index} className="border-b border-gray-100">
                          <td className="px-4 py-3 font-medium text-gray-900">
                            {item.scenario_a}
                          </td>

                          <td className="px-4 py-3 text-gray-700">
                            {item.scenario_b}
                          </td>

                          <td className="px-4 py-3 text-gray-700">
                            {(item.shared_dependencies || []).join(", ")}
                          </td>

                          <td className="px-4 py-3 font-semibold text-gray-900">
                            {Number(item.correlation_score || 0).toFixed(2)}
                          </td>

                          <td className="px-4 py-3">
                            <span
                              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                                item.severity === "High"
                                  ? "bg-red-100 text-red-700"
                                  : item.severity === "Moderate"
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-blue-100 text-blue-700"
                              }`}
                            >
                              {item.severity}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </SectionCard>
        )}

        {stressTests.length > 0 && (
          <SectionCard
            title="Portfolio Stress Testing"
            subtitle="FAIR-recalculated downside scenarios showing how portfolio exposure changes under adverse assumptions."
          >
            <div className="space-y-5">

              {stressTestingNarrative && topStressTest && (
                <div className={`rounded-xl border p-4 ${stressBannerStyle}`}>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
                    <div className="md:col-span-1">
                      <p className="text-xs uppercase tracking-wide opacity-70">
                        Primary Stress Driver
                      </p>

                      <p className="text-xl font-semibold mt-1">
                        {topStressTest.scenario}
                      </p>

                      <p className="text-2xl font-bold mt-2">
                        +{Number(topStressTest.delta_pct || 0).toFixed(1)}%
                      </p>

                      <p className="text-xs opacity-70 mt-1">
                        Expected annual loss increase
                      </p>
                    </div>

                    <div className="md:col-span-3">
                      <p className="text-sm font-semibold">
                        {stressTestingNarrative.title}
                      </p>

                      <p className="text-sm mt-1">
                        {stressTestingNarrative.summary}
                      </p>

                      <p className="text-sm mt-2">
                        {stressTestingNarrative.strategic_interpretation}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {stressTests.map((test) => (
                  <div
                    key={test.scenario}
                    className="rounded-xl border bg-white p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-semibold text-slate-900">
                        {test.scenario}
                      </p>

                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${
                          test.severity === "critical"
                            ? "bg-red-100 text-red-700"
                            : test.severity === "high"
                            ? "bg-amber-100 text-amber-700"
                            : "bg-blue-100 text-blue-700"
                        }`}
                      >
                        {test.severity}
                      </span>
                    </div>

                    <p className="text-xs text-slate-500 mt-2">
                      {test.description}
                    </p>

                    <div className="mt-4">
                      <p className="text-xs uppercase tracking-wide text-slate-500">
                        EAL Increase
                      </p>

                      <p className="text-2xl font-semibold text-slate-900 mt-1">
                        +{Number(test.delta_pct || 0).toFixed(1)}%
                      </p>
                    </div>

                    <div className="mt-3 text-xs text-slate-500">
                      {formatMoney(test.baseline_eal, settings.currency)} →{" "}
                      <strong className="text-slate-700">
                        {formatMoney(test.stressed_eal, settings.currency)}
                      </strong>
                    </div>

                    <p className="text-xs text-slate-400 mt-3">
                      Basis: {test.basis}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </SectionCard>
        )}

        {analysisLoading && !hasPortfolioData && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
            Running portfolio analysis. Historical data and dashboard layout are already available.
          </div>
       
        )}

        {!reportMode && (
          <SectionCard
            title="Investment Decision"
            subtitle="Evaluate how different investment levels reduce expected annual loss."
            actions={
              optimalPoint && recommendedRange ? (
              <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-900">
                <div className="text-xs opacity-70 mb-1">
                  Investment Guidance
                </div>

                <div>
                  <span className="font-semibold">
                    Efficient range:
                  </span>
                  {" "}
                  {formatMoney(recommendedRange.min, settings.currency)}
                  {" – "}
                  {formatMoney(recommendedRange.max, settings.currency)}
                </div>

                <div className="mt-1">
                  <span className="font-semibold">
                    Current position:
                  </span>
                  {" "}
                  {investmentStatus?.label || "Not evaluated"}
                </div>

                {effectiveInvestment > 0 && (
                  <div className="mt-1">
                    <span className="font-semibold">
                      Expected reduction:
                    </span>
                    {" "}
                    {formatMoney(ealReduction, settings.currency)}
                  </div>
                )}
              </div>
              ) : null
            }
          >

          <div
            className={`rounded-xl border p-5 ${investmentDecisionStyle.bg} ${investmentDecisionStyle.border}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">
                  Investment Decision Summary
                </p>

                <h3
                  className={`text-xl font-semibold mt-2 ${investmentDecisionStyle.text}`}
                >
                  {investmentDecision.title}
                </h3>

                <p className="text-sm text-slate-600 mt-2 max-w-3xl">
                  {investmentDecision.description}
                </p>
              </div>

              <span
                className={`px-4 py-2 rounded-xl text-sm font-semibold ${investmentDecisionStyle.badge}`}
              >
                {investmentDecision.badge}
              </span>
            </div>
          </div>

          <div className="relative">
            {analysisLoading && !pageLoading && (
              <div className="fixed inset-0 z-[9999] flex items-center justify-center">
                <LoadingOrbit
                  message=""
                  submessage=""
                />
              </div>
            )}
            <div className="rounded-3xl bg-sky-900 p-8 mb-4 mt-4 text-white shadow-xl">
              <div className="flex items-center gap-6">
                
                {/* Investment Value */}
                <div className="min-w-[220px]">
                  <div className="text-sm uppercase tracking-wide text-gray-200">
                    Current Investment
                  </div>

                  <div className="text-4xl font-bold mt-2">
                    {formatMoney(selectedInvestment, settings.currency)}
                  </div>
                </div>

                {/* Slider */}
                <div className="flex-1">

                  <input
                    type="range"
                    min={FAIR_INVESTMENT_CONFIG.min}
                   /* max={FAIR_INVESTMENT_CONFIG.max}  */
                    max={dynamicInvestmentMax}
                    step={investmentStep}
                    value={selectedInvestment}
                    onChange={(e) =>
                      setSelectedInvestment(normalizeInvestment(e.target.value))
                    }
                    className="
                      w-full h-2 rounded-lg appearance-none cursor-pointer
                      bg-gray-200
                      [&::-webkit-slider-thumb]:appearance-none
                      [&::-webkit-slider-thumb]:h-6
                      [&::-webkit-slider-thumb]:w-6
                      [&::-webkit-slider-thumb]:rounded-full
                      [&::-webkit-slider-thumb]:bg-cyan-400
                      [&::-webkit-slider-thumb]:shadow-lg
                      [&::-webkit-slider-thumb]:border-4
                      [&::-webkit-slider-thumb]:border-[#0B132B]
                    "
                  />

                  <div className="flex justify-between mt-4 text-sm text-gray-200">
                    <span>{formatMoney(0, settings.currency)}</span>
                    {/*<span>{formatMoney(FAIR_INVESTMENT_CONFIG.max, settings.currency)}</span>*/}
                    <span>{formatMoney(dynamicInvestmentMax, settings.currency)}</span>
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-4">
                    <p className="text-sm text-slate-100" role="status">
                      {selectedInvestment !== investment ? "Changes not yet applied" : "Current assumptions applied"}
                    </p>
                    <button
                      type="button"
                      onClick={() => setInvestment(normalizeInvestment(selectedInvestment))}
                      disabled={analysisLoading || selectedInvestment === investment}
                      className="rounded-xl bg-cyan-400 px-4 py-2 text-sm font-semibold text-sky-950 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {analysisLoading ? "Running..." : "Run Analysis"}
                    </button>
                  </div>
                </div>

              </div>
            </div>

            <div className="mt-6 mb-3">
              <h3 className="text-xl font-semibold text-gray-900">Investment vs Risk Reduction</h3>
              <p className="text-xs text-gray-500">
                Shows indicative risk reduction across investment levels. The selected investment metrics are calculated using the configured simulation precision.
              </p>
            </div>

            <div className="h-[320px] text-xs">
              {investmentCurveLoading ? (
                <div className="h-full flex items-center justify-center text-sm text-gray-500">Loading investment analysis...</div>
              ) : curveWithCurrent.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-gray-500">
                  Investment curve will appear once the analysis is ready.
                </div>
              ) : (
                <>
                <div className="flex flex-wrap gap-4 mb-3 text-xs text-slate-600">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-0.5 border-t-2 border-dashed border-orange-400"></span>
                    <span>
                      Max scenario evaluated ({formatMoney(optimalInvestment, settings.currency)})
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="w-3 h-0.5 border-t-2 border-dashed border-green-500"></span>
                    <span>
                      Current investment ({formatMoney(investment, settings.currency)})
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 border border-blue-400 bg-blue-50"></span>
                    <span>
                      Recommended range ({formatMoney(recommendedRange.min, settings.currency)} – {formatMoney(recommendedRange.max, settings.currency)})
                    </span>
                  </div>
                </div>

                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={curveWithCurrent} margin={{ top: 15, right: 30, left: 20, bottom: 25 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="investment" type="number" domain={[0, dynamicInvestmentMax]} tickFormatter={(value) => formatMoney(value, settings.currency)} />
                    <YAxis tickFormatter={(value) => formatMoney(value, settings.currency)} />
                    <Tooltip
                      formatter={(value) => formatMoney(value, settings.currency)}
                      labelFormatter={(label) => `Investment: ${formatMoney(Number(label), settings.currency)}`}

                    />
                    <Legend />

                    {recommendedRange && (
                      <ReferenceArea
                        x1={recommendedRange.min}
                        x2={recommendedRange.max}
                        fill="#DBEAFE"
                        fillOpacity={0.45}
                        stroke="#2563EB"
                        strokeOpacity={0.25}
                        ifOverflow="extendDomain"
                      />
                    )}

                    <Line type="monotone" dataKey="riskReduction" name="Risk Reduction" stroke="#2563EB" strokeWidth={3} dot={false} />

                    {investment > 0 && simulation && (
                      <>
                        <ReferenceLine
                          x={investment}
                          stroke="#16A34A"
                          strokeDasharray="4 4"
                         
                        />
                        {/*<ReferenceDot x={investment} y={ealReduction} r={6} fill="#16A34A" stroke="#16A34A" />  */}
                      </>
                    )}

                    {optimalPoint && (
                      <>
                        <ReferenceLine
                          x={optimalInvestment}
                          stroke="#DC2626"
                          strokeDasharray="4 4"
                       
                        />
                        {/* <ReferenceDot x={optimalInvestment} y={optimalReduction} r={7} fill="#DC2626" stroke="#DC2626" />  */}

                        {diminishingPoint && (
                          <>
                            <ReferenceLine
                              x={diminishingPoint.investment}
                              stroke="#F59E0B"
                              strokeDasharray="4 4"
                            
                            />
                            <ReferenceDot
                              x={diminishingPoint.investment}
                              y={diminishingPoint.riskReduction}
                              r={6}
                              fill="#F59E0B"
                              stroke="#F59E0B"
                            />
                          </>
                        )}
                      </>
                    )}
                  </LineChart>
                </ResponsiveContainer>
                </>
              )}
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => navigate("/executive-dashboard")}
                className="text-sm text-blue-700 hover:underline font-medium"
              >
                Open Executive Risk Dashboard →
              </button>
            </div>

           </div>

          </SectionCard>
        )}

      </div>

      </>


    </div>
  );


}

/* ------------------ subcomponents ------------------ */

function SectionCard({ title, subtitle, children, actions }) {
  return (
    <div className="bg-white border rounded-2xl p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        {actions && <div>{actions}</div>}
      </div>
      {children}
    </div>
  );
}

function Kpi({ title, value, subtitle, tone = "default" }) {
  return (
    <div className="px-6 py-4">
      <div className="text-[11px] uppercase tracking-[0.12em] text-gray-500">{title}</div>
      <div className={`text-xl font-semibold tracking-tight mt-2 ${getToneText(tone)}`}>{value}</div>
      {subtitle && <div className="text-xs text-gray-500 mt-2">{subtitle}</div>}
    </div>
  );
}
